import React from "react";
import {TablePage} from "../../model/utils/TablePage";
import {
    ProductionOrderExecutionJournalRow,
    ProductionTaskStatus
} from "../../model/production-order-execution/ProductionOrderExecution";
import {Table, TableColumn} from "../tables/Table";
import {useNavigate} from "react-router-dom";
import {useFiltersActions} from "../../hooks/useFiltersActions";
import {TableFilterKey} from "../../model/filters/TableFiltersDefinitions";
import {getTableRowBgColor, getTableRowTitle} from "../utils/ProductionOrderExecution";
import {PiListNumbers} from "react-icons/pi";

interface TableProps {
    data: TablePage<ProductionOrderExecutionJournalRow>
}

export function ProductionOrderExecutionJournalTable(props: TableProps) {
    const {updateFilterPage} = useFiltersActions()

    const navigate = useNavigate()

    const workOrderNumberColumn: TableColumn<ProductionOrderExecutionJournalRow> = {
        columnName: '№ Work',
        extractCellContent: row => row.workOrderNumber
    }

    const priorityColumn: TableColumn<ProductionOrderExecutionJournalRow> = {
        columnName: <PiListNumbers style={{height: '16px', marginTop: '1px'}}/>,
        title: 'Номер в очереди',
        extractCellContent: row => row.priority
    }

    const productionLineColumn: TableColumn<ProductionOrderExecutionJournalRow> = {
        columnName: 'Пр. линия',
        title: 'Производственная линия',
        extractCellContent: row => row.productionLine.name
    }

    const mainProductColumn: TableColumn<ProductionOrderExecutionJournalRow> = {
        columnName: 'Основная продукция',
        extractCellContent: row => `${row.mainProduct.nomenclature.name}: ${row.mainProduct.quantity} ${row.mainProduct.nomenclature.measurementUnit.name}`
    }

    const additionalEquipmentColumn: TableColumn<ProductionOrderExecutionJournalRow> = {
        columnName: 'Доп. комплектация',
        title: 'Дополнительная комплектация',
        displayAtList: true,
        additionalStyles: 'max-w-[350px] w-max',
        extractCellContent: row => row.additionalEquipment.length === 0 ? "–" : row.additionalEquipment.map(equipment => `${equipment.nomenclature.name}, ${equipment.quantity} ${equipment.nomenclature.measurementUnit.name}`)
    }

    function actionOnRowClick(row: ProductionOrderExecutionJournalRow) {
        navigate(`/production-order/execution?id=${row.documentNumber}`)
    }

    function sortRows(rows: ProductionOrderExecutionJournalRow[]): ProductionOrderExecutionJournalRow[][] {
        const inProductionOrders = rows.filter(row => row.executionStatus === ProductionTaskStatus.IN_PRODUCTION)
        const inLineOrders = rows.filter(row => row.executionStatus === ProductionTaskStatus.IN_LINE)
        const unavailableOrders = rows.filter(row => row.executionStatus === ProductionTaskStatus.UNAVAILABLE)
        const completedOrders = rows.filter(row => row.executionStatus === ProductionTaskStatus.COMPLETED)

        return [inProductionOrders, inLineOrders, unavailableOrders, completedOrders]
    }

    return (
        <Table<ProductionOrderExecutionJournalRow>
            tableColumns={[priorityColumn, workOrderNumberColumn, productionLineColumn, mainProductColumn, additionalEquipmentColumn]}
            rows={props.data.rows}
            onRowClick={actionOnRowClick}
            getTableRowBgColor={(row: ProductionOrderExecutionJournalRow) => getTableRowBgColor(row.executionStatus)}
            getTableRowTitle={(row: ProductionOrderExecutionJournalRow) => getTableRowTitle(row.executionStatus)}
            tableFilterKey={TableFilterKey.PRODUCTION_ORDER_EXECUTION_JOURNAL_FILTER}
            pagination={{
                currentPageNumber: props.data.pageNumber,
                quantityOfPages: props.data.pageCount,
                setPageNumber: (newPageNumber) => updateFilterPage({
                    key: TableFilterKey.PRODUCTION_ORDER_EXECUTION_JOURNAL_FILTER,
                    value: newPageNumber
                })
            }}
            sortRows={{
                sort: sortRows,
                separators: [
                    <tr>
                        <td colSpan={6}
                            className='whitespace-nowrap text-center pb-1 border-t-[0.5px] border-t-black/[.20]'>В
                            работе
                        </td>
                    </tr>,
                    <tr>
                        <td colSpan={6}
                            className='whitespace-nowrap text-center py-1 border-t-[0.5px] border-t-black/[.20]'>Ожидают
                            исполнения
                        </td>
                    </tr>,
                    <tr>
                        <td colSpan={6}
                            className='whitespace-nowrap text-center py-1 border-t-[0.5px] border-t-black/[.20]'>Недоступны
                            к исполнению
                        </td>
                    </tr>,
                    <tr>
                        <td colSpan={6}
                            className='whitespace-nowrap text-center py-1 border-t-[0.5px] border-t-black/[.20]'>Исполненные
                            на данном этапе
                        </td>
                    </tr>
                ]
            }}
        />
    )
}